import type { CSSProperties, ReactNode } from 'react';

/**
 * The shared visual vocabulary — surfaces, buttons, badges, headings, empty
 * states.
 *
 * WHY THIS EXISTS. The owner reviewed the running product and said the UI looks
 * "like plain paper". The measurable cause was not taste, it was structure:
 * **419 hand-rolled `CSSProperties` objects across 37 dashboard route files**,
 * against 14 imports from this package. Every screen invented its own card,
 * its own button, its own spacing. So nothing could be consistent, and raising
 * the bar meant editing thirty-seven files by hand — which is exactly why one
 * screen got fixed and the rest stayed flat (G30).
 *
 * Rules this package must keep:
 *   - **Tokens only.** Every colour, radius and space comes from a CSS custom
 *     property. `packages/ui` is shared with the WHITE-LABEL customer app,
 *     where a hard-coded brand colour is a rule violation, not a preference.
 *     A tenant re-themes by changing variables; nothing here needs to know.
 *   - **No copy.** These take `children`; they never contain a user-visible
 *     string, so they cannot leak untranslated English into any app.
 *   - **States are structural.** Elevation, tone and emphasis are props, not
 *     something each caller re-derives — that is how "which of these three
 *     buttons is destructive" stops being a coin flip.
 */

// ── Surface ────────────────────────────────────────────────────────────────

export type SurfaceTone = 'neutral' | 'info' | 'success' | 'warn' | 'danger';

const TONE_RAIL: Record<SurfaceTone, string> = {
  neutral: 'var(--color-border-strong, rgba(15,23,42, 0.16))',
  info: 'var(--color-info, #1d4ed8)',
  success: 'var(--color-primary, #475569)',
  warn: 'var(--color-warn, #b45309)',
  danger: 'var(--color-danger, #b91c1c)',
};

const TONE_TINT: Record<SurfaceTone, string> = {
  neutral: 'var(--color-surface-sunken, rgba(15,23,42, 0.06))',
  info: 'var(--color-info-bg, rgba(29,78,216,0.08))',
  success: 'var(--color-success-bg, rgba(22,163,74, 0.09))',
  warn: 'var(--color-warn-bg, rgba(180,83,9,0.10))',
  danger: 'var(--color-danger-bg, rgba(185,28,28,0.09))',
};

const TONE_FG: Record<SurfaceTone, string> = {
  neutral: 'var(--color-on-surface-muted)',
  info: 'var(--color-info, #1d4ed8)',
  success: 'var(--color-primary, #475569)',
  warn: 'var(--color-warn, #b45309)',
  danger: 'var(--color-danger, #b91c1c)',
};

/**
 * A panel. `level` is how far off the page it sits — 0 is flush (a row inside
 * another surface), 1 is a normal card, 2 is a card that should feel liftable.
 * `accent` paints a top rail, which is how a column or card carries status
 * without the reader parsing a heading first.
 */
/**
 * The value of the `data-lift` attribute for a surface.
 *
 * Exported so its test covers the REAL binding. The package has no DOM test
 * environment, and a test that reimplemented this rule would pass even after
 * the component stopped following it — which is the failure mode the rule
 * exists to prevent.
 */
export function liftAttr(interactive: boolean | undefined): '' | undefined {
  return interactive ? '' : undefined;
}

export function Surface({
  children,
  level = 1,
  accent,
  padded = true,
  interactive = false,
  as: Tag = 'div',
  style,
  ...rest
}: {
  readonly children: ReactNode;
  readonly level?: 0 | 1 | 2;
  /**
   * Whether this surface is something the viewer can act on.
   *
   * Drives the pointer lift, and nothing else. Lift used to be inferred from
   * `level === 2`, which conflated two independent things: elevation is how
   * deep a surface sits, interactivity is whether pressing it does anything. A
   * card can be raised and inert, or flat and clickable.
   *
   * The conflation shipped a false affordance. The only `level={2}` surface in
   * the product is a Kanban COLUMN on the operator queue board — a container
   * for tickets — so on a desktop, moving the pointer across the board made
   * every column rise as though it were a button. Nothing happened when you
   * clicked.
   *
   * Defaults off. A surface has to say it is interactive; it cannot acquire the
   * affordance by being visually prominent.
   */
  readonly interactive?: boolean;
  readonly accent?: SurfaceTone;
  readonly padded?: boolean;
  /**
   * The element to render. A panel is usually a landmark — most call sites are
   * `<section>` — and converting them all to `<div>` would strip that from
   * screen readers to save a prop. Semantics are the caller's to state.
   */
  readonly as?: 'div' | 'section' | 'article' | 'aside';
  readonly style?: CSSProperties;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'style' | 'children'>) {
  const shadow = level === 0 ? 'none' : level === 1 ? 'var(--shadow-2)' : 'var(--shadow-3)';
  return (
    <Tag
      {...rest}
      data-lift={liftAttr(interactive)}
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border, rgba(15,23,42, 0.08))',
        ...(accent ? { borderTop: `3px solid ${TONE_RAIL[accent]}` } : null),
        boxShadow: shadow,
        padding: padded ? 'var(--space-4)' : 0,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

// ── Heading ────────────────────────────────────────────────────────────────

/**
 * A section title with an optional status dot and a trailing slot (a count, an
 * action). Exists so "title on the left, number on the right" is decided once
 * rather than re-argued per screen.
 */
export function SectionHeading({
  children,
  dot,
  trailing,
  as: Tag = 'h2',
}: {
  readonly children: ReactNode;
  readonly dot?: SurfaceTone;
  readonly trailing?: ReactNode;
  readonly as?: 'h1' | 'h2' | 'h3';
}) {
  return (
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-3)',
      }}
    >
      <Tag
        style={{
          fontSize: Tag === 'h1' ? 'var(--text-h1)' : 'var(--text-body)',
          fontWeight: 650,
          letterSpacing: '-0.01em',
          margin: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5em',
          minWidth: 0,
        }}
      >
        {dot ? (
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: TONE_RAIL[dot],
              flexShrink: 0,
            }}
          />
        ) : null}
        {children}
      </Tag>
      {trailing}
    </header>
  );
}

// ── Badge ──────────────────────────────────────────────────────────────────

/** A count or status pill. Tabular figures so columns of numbers line up. */
export function Badge({
  children,
  tone = 'neutral',
}: {
  readonly children: ReactNode;
  readonly tone?: SurfaceTone;
}) {
  return (
    <span
      style={{
        fontSize: '0.6875rem',
        fontWeight: 600,
        color: TONE_FG[tone],
        background: TONE_TINT[tone],
        borderRadius: 'var(--radius-pill)',
        padding: '0.15em 0.6em',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}
    >
      {children}
    </span>
  );
}

// ── Button ─────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';

/**
 * `variant` is the whole point. Before this, a screen's primary action, its
 * reversible secondary and its DESTRUCTIVE action were three identical grey
 * boxes — on the queue board, "Hold" and "Cancel" were indistinguishable, and
 * one of them throws the ticket away. Emphasis is now a prop, so it cannot be
 * forgotten.
 */
/**
 * How each button variant looks.
 *
 * Hoisted to module scope so it can be ASSERTED on: the design language carried
 * a `danger` variant that almost nothing used, while "Delete" rendered
 * identically to the "Edit" beside it. Adoption fixed the call sites; a test
 * over this table is what stops the variants collapsing back into each other.
 *
 * It was also being rebuilt on every render, so this is one less allocation per
 * button as a side effect.
 */
export const BUTTON_VARIANT_STYLES: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: 'var(--color-primary)',
    color: 'var(--color-primary-fg)',
    border: '1px solid transparent',
  },
  secondary: {
    background: 'var(--color-surface)',
    color: 'var(--color-on-surface)',
    border: '1px solid var(--color-border-strong, rgba(15,23,42, 0.16))',
  },
  quiet: {
    background: 'transparent',
    color: 'var(--color-on-surface-muted)',
    border: '1px solid transparent',
  },
  danger: {
    background: 'transparent',
    color: 'var(--color-danger, #b91c1c)',
    border: '1px solid transparent',
    fontWeight: 500,
  },
};

export function Button({
  children,
  variant = 'secondary',
  full,
  style,
  ...rest
}: {
  readonly children: ReactNode;
  readonly variant?: ButtonVariant;
  readonly full?: boolean;
  readonly style?: CSSProperties;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'children'>) {
  const base: CSSProperties = {
    minHeight: 'var(--touch-target, 44px)',
    padding: '0 1em',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-meta)',
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4em',
    whiteSpace: 'nowrap',
    transition: 'background 160ms ease, border-color 160ms ease, opacity 160ms ease',
    ...(full ? { width: '100%' } : null),
  };
  return (
    <button
      {...rest}
      className="k-press"
      style={{ ...base, ...BUTTON_VARIANT_STYLES[variant], ...style }}
    >
      {children}
    </button>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────

/**
 * Every empty surface must say what it is and what to do next — the
 * no-dead-ends rule. Taking `action` as a slot makes the next step part of the
 * component's shape rather than something a screen may or may not remember.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  readonly title: ReactNode;
  readonly body?: ReactNode;
  readonly action?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-6) var(--space-4)',
        color: 'var(--color-on-surface-muted)',
      }}
    >
      <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-on-surface)' }}>{title}</p>
      {body ? (
        <p style={{ margin: 0, fontSize: 'var(--text-meta)', maxWidth: '34ch' }}>{body}</p>
      ) : null}
      {action ? <div style={{ marginTop: 'var(--space-2)' }}>{action}</div> : null}
    </div>
  );
}

// ── Canonical style objects ────────────────────────────────────────────────
//
// The components above are the right way to build NEW screens. These exist for
// the thirty-seven that already exist.
//
// A survey of `apps/dashboard/src/routes` found the same handful of style
// objects declared over and over by hand: `ghostLinkStyle` in 25 files,
// `primaryButtonStyle` in 24, `cardStyle` in 23, `inputStyle` in 22,
// `pageStyle` in 16. They are not identical — `cardStyle` in billing.tsx uses a
// shadow and `--space-5`, while catalog.tsx uses a border and different
// padding. Same name, different card, on screens sitting next to each other in
// the same product. That IS the inconsistency the owner sees.
//
// Importing one definition kills the drift immediately and is a safe,
// mechanical change per file, where swapping every element for a component is
// neither. New work should still reach for the components.

/** The standard panel. Canonical form of the 23 hand-written copies. */
export const cardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  border: '1px solid var(--color-border, rgba(15,23,42, 0.08))',
  boxShadow: 'var(--shadow-2)',
};

/** Page wrapper: the readable-width column every screen re-derived. */
export const pageStyle: CSSProperties = {
  maxWidth: 1200,
  margin: '0 auto',
  padding: 'var(--space-6) var(--space-5)',
};

/** The affirmative action. */
export const primaryButtonStyle: CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '0 1em',
  minHeight: 'var(--touch-target, 44px)',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-sm)',
  fontWeight: 600,
  fontSize: 'var(--text-meta)',
  fontFamily: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/** The reversible action beside it. */
export const ghostButtonStyle: CSSProperties = {
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  padding: '0 1em',
  minHeight: 'var(--touch-target, 44px)',
  border: '1px solid var(--color-border-strong, rgba(15,23,42, 0.16))',
  borderRadius: 'var(--radius-sm)',
  fontWeight: 600,
  fontSize: 'var(--text-meta)',
  fontFamily: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * The destructive action. There was no shared one — six screens rolled a
 * `dangerButtonStyle` and the rest simply reused the ghost style, which is how
 * "Cancel" ended up looking exactly like "Hold".
 */
export const dangerButtonStyle: CSSProperties = {
  background: 'transparent',
  color: 'var(--color-danger, #b91c1c)',
  padding: '0 0.9em',
  minHeight: 'var(--touch-target, 44px)',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-sm)',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

/** A quiet navigational link rendered as a chip. */
export const ghostLinkStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  textDecoration: 'none',
  padding: '0 0.75em',
  minHeight: 'var(--touch-target, 44px)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border, rgba(15,23,42, 0.08))',
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
};

/**
 * A row of quiet links, usually in a page header.
 *
 * `flexWrap` is the whole point. Three ghost links at 110px each need 330px
 * plus gaps; a phone gives about 358px of content width, and a header that also
 * carries a title has less. Without wrapping, the last link hangs off the right
 * edge and the page scrolls sideways — which is exactly what `/marketing` did.
 * Wrapping is the only behaviour that stays correct as copy grows, and copy
 * here is translated into four languages, so it will grow.
 */
export const linkRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
};

/**
 * A checkbox or radio and its label as one tap target.
 *
 * A native checkbox is 13-16px. Wrapping it in a label gives it a wide hit box
 * but not a tall one: the measured rows on `/branding/hours` were 22px, which
 * is half a fingertip. `minHeight` on the label, not the input, is what fixes
 * it — the box stays its native size while the row that receives the tap grows
 * to a real target.
 */
export const checkboxRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minHeight: 'var(--touch-target, 44px)',
  cursor: 'pointer',
};

/**
 * A standalone navigational link — "Back to queue" and friends.
 *
 * Distinct from a link inside a sentence, which WCAG 2.5.8 exempts from target
 * sizing because shrinking body copy to fit fingers would be worse. This one
 * stands alone, so it gets a real target.
 */
export const backLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 'var(--touch-target, 44px)',
  color: 'var(--color-on-surface-muted)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  textDecoration: 'none',
};

/**
 * Strips browser button chrome, for a back control that must be a `<button>`.
 *
 * Some "back" controls cannot be links: a real back steps through history
 * rather than navigating to a fixed path, and history is not a URL. Those have
 * to be buttons — which arrive with a border, a background and the browser's
 * own font, none of which belong on something that reads as a link.
 *
 * Spread AFTER `backLinkStyle`:
 *
 *     style={{ ...backLinkStyle, ...backButtonReset }}
 *
 * Lives here rather than in the app that needed it first, because a bespoke
 * copy per app is exactly what the design-language ratchet in
 * `scripts/check-gaps.mjs` counts — and it caught this one the day it appeared.
 */
export const backButtonReset: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
};

/** Text input / select. */
export const inputStyle: CSSProperties = {
  padding: '12px 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border-strong, rgba(15,23,42, 0.16))',
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
};

/** The small uppercase label above a heading. */
export const eyebrowStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  margin: 0,
  fontWeight: 600,
};
